/**
 * normalizeQuote — read-only presentation layer normalization.
 *
 * CleanBid quotes exist in two Firestore formats:
 *   - Workspace quotes: { lineItems: [...] }
 *   - Calculator quotes: { services: { roof: {...}, gutters: {...}, ... } }
 *
 * This utility converts both to a canonical lineItems[] structure so all
 * UI components can render a single format. Firestore documents are NEVER
 * modified — normalization happens only at read time.
 *
 * @param {Object|null} quote - Raw quote document from Firestore
 * @returns {Object} Quote with guaranteed lineItems[] array
 */
export const normalizeQuote = (quote) => {
  if (!quote) return quote;

  // Already has lineItems — return unchanged
  if (Array.isArray(quote.lineItems) && quote.lineItems.length > 0) {
    return quote;
  }

  // Convert services{} → lineItems[]
  const services = quote.services || {};
  const entries = Object.entries(services).filter(([, svc]) => svc != null);

  if (entries.length === 0) {
    // Neither format has data — return with empty lineItems
    return { ...quote, lineItems: [] };
  }

  const lineItems = entries.map(([key, svc]) => {
    // Amount extraction: same chain used by WorkOrderSheet's legacy parser
    const amount = svc.price ?? svc.amount ?? svc.result?.grandTotal ?? svc.result?.price ?? 0;

    // Human-readable description from measurement inputs
    const measurement = svc.input?.sqFt
      ? `${svc.input.sqFt} sq ft`
      : svc.input?.linearFeet
      ? `${svc.input.linearFeet} linear ft`
      : null;

    // Convert snake_case service key to Title Case (e.g. pest_control → Pest Control)
    const titleName = key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    // For standard services the title already includes 'Cleaning'; flat-rate IDs do not
    const displayName = titleName.includes(' ')
      ? titleName                // e.g. "Pest Control", "Lawn Care"
      : `${titleName} Cleaning`; // e.g. "Roof Cleaning", "Gutters Cleaning"

    return {
      name: displayName,
      description: svc.description || measurement || null,
      quantity: 1,
      unitPrice: amount,
      total: amount,
      price: amount,        // alias used by some legacy renderers
      _source: 'calculator', // provenance tag — read-only, never written to Firestore
    };
  });

  return { ...quote, lineItems };
};
